import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Video, Phone, Mail, Calendar as CalIcon, Pencil, AlertCircle, Save, Send } from "lucide-react";

const blankCreate = () => ({
  client_id: "", package_id: "virtual_40", starts_at: "", zoom_link: "", location: "", notes: "",
});

const blankEdit = () => ({
  id: "", starts_at: "", zoom_link: "", session_notes: "", status: "scheduled", clear_starts_at: false,
});

function fmtLocalDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [clients, setClients] = useState([]);
  const [packages, setPackages] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [createForm, setCreateForm] = useState(blankCreate());
  const [editForm, setEditForm] = useState(blankEdit());
  const [activeBooking, setActiveBooking] = useState(null);

  const load = async () => {
    const [b, c, p] = await Promise.all([
      api.get("/bookings"), api.get("/clients"), api.get("/packages"),
    ]);
    setBookings(b.data); setClients(c.data); setPackages(p.data);
  };
  useEffect(() => { load(); }, []);

  const pending = useMemo(() => bookings.filter((b) => !b.starts_at && b.payment_status === "paid" && b.status !== "cancelled"), [bookings]);
  const scheduled = useMemo(() => bookings.filter((b) => b.starts_at).slice().sort((a, b) => (a.starts_at < b.starts_at ? -1 : 1)), [bookings]);
  const unpaidPending = useMemo(() => bookings.filter((b) => !b.starts_at && b.payment_status !== "paid" && b.status !== "cancelled"), [bookings]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...createForm };
      if (!payload.starts_at) delete payload.starts_at; // backend allows null
      await api.post("/bookings", payload);
      toast.success("Session added");
      setOpenCreate(false);
      setCreateForm(blankCreate());
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const startEdit = (b, opts = {}) => {
    setActiveBooking(b);
    setEditForm({
      id: b.id,
      starts_at: opts.forceScheduling ? "" : fmtLocalDateTime(b.starts_at),
      zoom_link: b.zoom_link || "",
      session_notes: b.session_notes || "",
      status: b.status || "scheduled",
      clear_starts_at: false,
    });
    setOpenEdit(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!activeBooking) return;
    try {
      const payload = {
        status: editForm.status,
        zoom_link: editForm.zoom_link || null,
        session_notes: editForm.session_notes || null,
      };
      if (editForm.clear_starts_at) {
        payload.clear_starts_at = true;
      } else if (editForm.starts_at) {
        // Convert datetime-local → ISO with timezone
        payload.starts_at = new Date(editForm.starts_at).toISOString();
      }
      await api.patch(`/bookings/${activeBooking.id}`, payload);
      toast.success(editForm.clear_starts_at ? "Moved back to Awaiting Scheduling" : "Booking updated");
      setOpenEdit(false);
      setActiveBooking(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const updateStatus = async (id, patch) => {
    try {
      await api.patch(`/bookings/${id}`, patch);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete booking? This cannot be undone.")) return;
    await api.delete(`/bookings/${id}`);
    load();
  };

  const resendEmail = async (b) => {
    const kindHint = b.starts_at ? "session-scheduled email (with Zoom link)" : "booking confirmation";
    if (!window.confirm(`Resend the ${kindHint} to ${b.client_email}?`)) return;
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/resend-email`);
      toast.success(`Sent ${data.kind} email to ${data.to}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-8 md:p-12" data-testid="bookings-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2">Schedule</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight">Bookings.</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-2xl">
            {pending.length > 0 ? <><strong className="text-[#FF331F]">{pending.length}</strong> awaiting your scheduling · </> : null}
            <strong className="text-white">{scheduled.length}</strong> scheduled
          </p>
        </div>
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="add-booking-btn">
              <Plus className="h-4 w-4 mr-2" /> New session
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white max-w-lg" data-testid="add-booking-dialog">
            <DialogHeader>
              <DialogTitle className="font-display uppercase">Schedule a session</DialogTitle>
              <DialogDescription className="text-zinc-500 text-xs uppercase tracking-widest">For manually-added sessions (existing clients).</DialogDescription>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Client</Label>
                <Select value={createForm.client_id} onValueChange={(v) => setCreateForm({ ...createForm, client_id: v })}>
                  <SelectTrigger className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="booking-client-trigger"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white">
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Package</Label>
                <Select value={createForm.package_id} onValueChange={(v) => setCreateForm({ ...createForm, package_id: v })}>
                  <SelectTrigger className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="booking-package-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white">
                    {packages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — ${p.amount}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Date & Time (leave blank to schedule later)</Label>
                <Input type="datetime-local" value={createForm.starts_at} onChange={(e) => setCreateForm({ ...createForm, starts_at: e.target.value })} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="booking-datetime" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Zoom link</Label>
                  <Input value={createForm.zoom_link} onChange={(e) => setCreateForm({ ...createForm, zoom_link: e.target.value })} placeholder="https://zoom.us/j/…" className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="booking-zoom" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Location</Label>
                  <Input value={createForm.location} onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })} placeholder="Gym address" className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="booking-location" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Notes</Label>
                <Textarea value={createForm.notes} onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })} rows={2} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="booking-notes" />
              </div>
              <Button type="submit" className="w-full bg-[#FF331F] hover:bg-[#FF5242] font-bold uppercase tracking-widest rounded-sm" data-testid="save-booking-btn">Save</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* ── AWAITING SCHEDULING (paid, no time yet) ── */}
      {pending.length > 0 && (
        <section className="mb-10" data-testid="pending-scheduling-section">
          <div className="flex items-center gap-3 mb-3">
            <AlertCircle className="h-4 w-4 text-[#FF331F]" />
            <h2 className="font-display uppercase text-lg tracking-widest text-white">Awaiting Scheduling</h2>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Paid · You need to contact them</span>
          </div>
          <div className="border border-[#FF331F]/40 bg-[#FF331F]/5">
            <table className="w-full text-sm" data-testid="pending-table">
              <thead className="border-b border-[#FF331F]/30 text-zinc-400 uppercase tracking-widest text-[10px]">
                <tr>
                  <th className="text-left p-4">Client</th>
                  <th className="text-left p-4">Contact</th>
                  <th className="text-left p-4">Package</th>
                  <th className="text-left p-4">Notes</th>
                  <th className="text-left p-4">Created</th>
                  <th className="p-4 w-44 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {pending.map((b) => (
                  <tr key={b.id} className="border-b border-[#FF331F]/15 last:border-0 hover:bg-[#FF331F]/8" data-testid={`pending-row-${b.id}`}>
                    <td className="p-4">
                      <div className="font-display uppercase text-white">{b.client_name}</div>
                      <div className="text-[11px] text-zinc-500">${Number(b.amount).toFixed(2)} · Paid</div>
                    </td>
                    <td className="p-4 text-xs">
                      {b.client_phone ? (
                        <a href={`tel:${b.client_phone}`} className="flex items-center gap-1.5 text-white hover:text-[#FF331F]"><Phone className="h-3 w-3" /> {b.client_phone}</a>
                      ) : <span className="text-zinc-600">No phone</span>}
                      <a href={`mailto:${b.client_email}`} className="flex items-center gap-1.5 text-zinc-400 hover:text-white mt-1"><Mail className="h-3 w-3" /> {b.client_email}</a>
                    </td>
                    <td className="p-4 text-zinc-300">{b.package_name}</td>
                    <td className="p-4 text-zinc-400 text-xs max-w-xs">{b.notes ? <span className="line-clamp-3 whitespace-pre-line">{b.notes}</span> : <span className="text-zinc-600">—</span>}</td>
                    <td className="p-4 text-zinc-500 text-xs">{new Date(b.created_at).toLocaleDateString()}</td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => resendEmail(b)} className="p-2 text-zinc-500 hover:text-[#FF331F]" data-testid={`resend-email-${b.id}`} title="Resend booking confirmation email">
                          <Send className="h-4 w-4" />
                        </button>
                        <Button onClick={() => startEdit(b, { forceScheduling: true })} className="bg-[#FF331F] hover:bg-[#FF5242] font-bold uppercase tracking-widest text-xs rounded-sm h-8" data-testid={`schedule-btn-${b.id}`}>
                          <CalIcon className="h-3.5 w-3.5 mr-1.5" /> Schedule
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── SCHEDULED ── */}
      <section data-testid="scheduled-section">
        <div className="flex items-center gap-3 mb-3">
          <CalIcon className="h-4 w-4 text-zinc-400" />
          <h2 className="font-display uppercase text-lg tracking-widest text-white">Scheduled Sessions</h2>
        </div>
        <div className="border border-[#27272A] bg-[#0F0F0F]">
          <table className="w-full text-sm" data-testid="bookings-table">
            <thead className="border-b border-[#27272A] text-zinc-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="text-left p-4">When</th>
                <th className="text-left p-4">Client</th>
                <th className="text-left p-4">Package</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Payment</th>
                <th className="p-4 w-40 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {scheduled.length === 0 && unpaidPending.length === 0 ? (
                <tr><td colSpan={6} className="p-12 text-center text-zinc-500">No scheduled sessions yet.</td></tr>
              ) : scheduled.map((b) => (
                <tr key={b.id} className="border-b border-[#27272A] hover:bg-[#121212]" data-testid={`booking-row-${b.id}`}>
                  <td className="p-4 text-zinc-300">
                    <div className="font-display uppercase">{new Date(b.starts_at).toLocaleDateString()}</div>
                    <div className="text-xs text-zinc-500">{new Date(b.starts_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                  </td>
                  <td className="p-4">
                    <div className="font-display uppercase">{b.client_name}</div>
                    <div className="text-xs text-zinc-500">{b.client_email}</div>
                    {b.client_phone && <div className="text-[11px] text-zinc-600 mt-0.5">{b.client_phone}</div>}
                  </td>
                  <td className="p-4 text-zinc-400">
                    {b.package_name}
                    {b.zoom_link && <a href={b.zoom_link} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center text-[#FF331F] text-xs" data-testid={`zoom-link-${b.id}`}><Video className="h-3 w-3 mr-1" />Zoom</a>}
                  </td>
                  <td className="p-4">
                    <select value={b.status} onChange={(e) => updateStatus(b.id, { status: e.target.value })} className="bg-[#050505] border border-[#27272A] text-xs uppercase tracking-widest px-2 py-1 text-zinc-300" data-testid={`booking-status-${b.id}`}>
                      <option value="scheduled">Scheduled</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="no_show">No-show</option>
                    </select>
                  </td>
                  <td className="p-4"><span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${b.payment_status === "paid" ? "bg-emerald-500/10 text-emerald-400" : "bg-zinc-800 text-zinc-400"}`}>{b.payment_status}</span></td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-1">
                      {b.payment_status === "paid" && (
                        <button onClick={() => resendEmail(b)} className="p-2 text-zinc-500 hover:text-[#FF331F]" data-testid={`resend-email-${b.id}`} title={b.starts_at ? "Resend session-scheduled email with Zoom link" : "Resend booking confirmation"}>
                          <Send className="h-4 w-4" />
                        </button>
                      )}
                      <button onClick={() => startEdit(b)} className="p-2 text-zinc-500 hover:text-white" data-testid={`edit-booking-${b.id}`} title="Edit / Reschedule">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(b.id)} className="p-2 text-zinc-500 hover:text-red-400" data-testid={`delete-booking-${b.id}`} title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {unpaidPending.length > 0 && (
                <>
                  <tr><td colSpan={6} className="px-4 pt-6 pb-2 text-[10px] uppercase tracking-widest text-zinc-600">Unpaid / Abandoned</td></tr>
                  {unpaidPending.map((b) => (
                    <tr key={b.id} className="border-b border-[#27272A] opacity-60 hover:bg-[#121212]" data-testid={`unpaid-row-${b.id}`}>
                      <td className="p-4 text-zinc-500 italic text-xs">—</td>
                      <td className="p-4">
                        <div className="font-display uppercase">{b.client_name}</div>
                        <div className="text-xs text-zinc-500">{b.client_email}</div>
                      </td>
                      <td className="p-4 text-zinc-400">{b.package_name}</td>
                      <td className="p-4"><span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-zinc-800 text-zinc-400">{b.status}</span></td>
                      <td className="p-4"><span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-zinc-800 text-zinc-400">{b.payment_status}</span></td>
                      <td className="p-4 text-right">
                        <button onClick={() => remove(b.id)} className="p-2 text-zinc-500 hover:text-red-400" data-testid={`delete-unpaid-${b.id}`} title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── EDIT / RESCHEDULE DIALOG ── */}
      <Dialog open={openEdit} onOpenChange={(o) => { setOpenEdit(o); if (!o) setActiveBooking(null); }}>
        <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white max-w-lg" data-testid="edit-booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">{activeBooking?.starts_at ? "Reschedule / Edit" : "Schedule Session"}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs uppercase tracking-widest">
              {activeBooking ? `${activeBooking.client_name} · ${activeBooking.package_name}` : ""}
            </DialogDescription>
          </DialogHeader>
          {activeBooking && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Session date &amp; time</Label>
                <Input
                  type="datetime-local"
                  value={editForm.starts_at}
                  onChange={(e) => setEditForm({ ...editForm, starts_at: e.target.value, clear_starts_at: false })}
                  className="bg-[#050505] border-[#27272A] rounded-sm"
                  data-testid="edit-starts-at"
                  required={!editForm.clear_starts_at}
                  disabled={editForm.clear_starts_at}
                />
                {activeBooking.starts_at && (
                  <label className="flex items-center gap-2 text-[11px] text-zinc-500 cursor-pointer pt-1">
                    <input
                      type="checkbox"
                      checked={editForm.clear_starts_at}
                      onChange={(e) => setEditForm({ ...editForm, clear_starts_at: e.target.checked, starts_at: e.target.checked ? "" : editForm.starts_at })}
                      className="accent-[#FF331F]"
                      data-testid="unschedule-checkbox"
                    />
                    Unschedule — move back to &ldquo;Awaiting Scheduling&rdquo;
                  </label>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white">
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="no_show">No-show</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Video className="h-3 w-3" /> Zoom link</Label>
                <Input type="url" value={editForm.zoom_link} onChange={(e) => setEditForm({ ...editForm, zoom_link: e.target.value })} placeholder="https://zoom.us/j/…" className="bg-[#050505] border-[#27272A] rounded-sm font-mono text-xs" data-testid="edit-zoom-link" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Private session notes</Label>
                <Textarea rows={3} value={editForm.session_notes} onChange={(e) => setEditForm({ ...editForm, session_notes: e.target.value })} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="edit-session-notes" />
              </div>
              <div className="text-[11px] text-zinc-500 border-t border-[#27272A] pt-3">
                <strong className="text-zinc-400">Heads up:</strong> Client will <em>not</em> receive an automated email yet (email integration coming soon). Be sure to contact {activeBooking.client_name} directly to confirm.
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setOpenEdit(false)} className="border-[#27272A] bg-[#050505] text-white hover:bg-[#121212] rounded-sm">Cancel</Button>
                <Button type="submit" className="bg-[#FF331F] hover:bg-[#FF5242] font-bold uppercase tracking-widest rounded-sm" data-testid="save-edit-booking-btn">
                  <Save className="h-4 w-4 mr-2" /> {editForm.clear_starts_at ? "Unschedule" : "Save"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
